import { DriverDto } from "@dtos/driver.dto";

export class BudgetDto {
    id: number;
    total_price: string;
    additional_services_price: string;
    departure_datetime: string;
    arrival_datetime: string;
    base_price: string;
    fuel_price: string;
    transport_cost_outbound: string;
    transport_cost_return: string;
    optional_expenses_price: string;
    accommodation_cost: string;
    ferry_cost: string;
    toll_expense: string;
    per_diem_cost: string;
    budget_status: string;
    invalidation_reason: string;
    driver: DriverDto  | null;
    leg: number | null;
    leg_id: number | null;
    transport_id: number | null;
    driver_id: string;
    driver_name: string;
    driver_last_name: string;
    driver_document: string;
    driver_rating: number;
    driver_email: string;
    driver_phone: string;
    margin_eur: number;
    margin_pct: number;
    driver_exceed_1500_euros: boolean;
    driver_has_another_transport: boolean | null;
    created_date: string | null;
    modified_date: string | null;
    state: boolean;
    driver_tags: string[] = [];
    driver_fortnight_earnings: number = 0;
    concurrent_transports: number[] = [];
    constructor(data: Partial<BudgetDto> = {}) {
        this.id = data.id ?? 0;
        this.total_price = BudgetDto.toStringValue(data.total_price, '0.00');
        this.additional_services_price = BudgetDto.toStringValue(data.additional_services_price, '0.00');
        this.departure_datetime = data.departure_datetime ?? '';
        this.arrival_datetime = data.arrival_datetime ?? '';
        this.base_price = BudgetDto.toStringValue(data.base_price, '0.00');
        this.fuel_price = BudgetDto.toStringValue(data.fuel_price, '0.00');
        this.transport_cost_outbound = BudgetDto.toStringValue(data.transport_cost_outbound, '0.00');
        this.transport_cost_return = BudgetDto.toStringValue(data.transport_cost_return, '0.00');
        this.optional_expenses_price = BudgetDto.toStringValue(data.optional_expenses_price, '0.00');
        this.accommodation_cost = BudgetDto.toStringValue(data.accommodation_cost, '0.00');
        this.ferry_cost = BudgetDto.toStringValue(data.ferry_cost, '0.00');
        this.toll_expense = BudgetDto.toStringValue(data.toll_expense, '0.00');
        this.per_diem_cost = BudgetDto.toStringValue(data.per_diem_cost, '0.00');
        this.budget_status = data.budget_status ?? '';
        this.invalidation_reason = data.invalidation_reason ?? '';
        this.driver = data.driver ?? null;
        this.leg = data.leg ?? null;
        this.leg_id = data.leg_id ?? null;
        this.transport_id = data.transport_id ?? null;
        this.driver_id = BudgetDto.toStringValue(data.driver_id, '');
        this.driver_name = data.driver_name ?? '';
        this.driver_last_name = data.driver_last_name ?? '';
        this.driver_document = data.driver_document ?? '';
        this.driver_rating = data.driver_rating ?? 0;
        this.driver_email = data.driver_email ?? '';
        this.driver_phone = data.driver_phone ?? '';
        this.margin_eur = data.margin_eur ?? 0;
        this.margin_pct = data.margin_pct ?? 0;
        this.driver_exceed_1500_euros = data.driver_exceed_1500_euros ?? false;
        this.driver_has_another_transport = data.driver_has_another_transport ?? null;
        this.created_date = data.created_date ?? null;
        this.modified_date = data.modified_date ?? null;
        this.state = data.state ?? false;
        this.driver_tags = data.driver_tags ?? [];
        this.driver_fortnight_earnings = data.driver_fortnight_earnings ?? 0;
        this.concurrent_transports = data.concurrent_transports ?? [];
    }

    static fromJson(json: any): BudgetDto {
        return new BudgetDto(json);
    }

    toJson(): any {
        return {
            id: this.id,
            total_price: this.total_price,
            additional_services_price: this.additional_services_price,
            departure_datetime: this.departure_datetime,
            arrival_datetime: this.arrival_datetime,
            base_price: this.base_price,
            fuel_price: this.fuel_price,
            transport_cost_outbound: this.transport_cost_outbound,
            transport_cost_return: this.transport_cost_return,
            optional_expenses_price: this.optional_expenses_price,
            accommodation_cost: this.accommodation_cost,
            ferry_cost: this.ferry_cost,
            toll_expense: this.toll_expense,
            per_diem_cost: this.per_diem_cost,
            budget_status: this.budget_status,
            invalidation_reason: this.invalidation_reason,
            driver: this.driver,
            leg: this.leg,
            leg_id: this.leg_id,
            transport_id: this.transport_id,
            driver_id: this.driver_id,
            driver_name: this.driver_name,
            driver_last_name: this.driver_last_name,
            driver_document: this.driver_document,
            driver_rating: this.driver_rating,
            driver_email: this.driver_email,
            driver_phone: this.driver_phone,
            margin_eur: this.margin_eur,
            margin_pct: this.margin_pct,
            driver_exceed_1500_euros: this.driver_exceed_1500_euros,
            driver_has_another_transport: this.driver_has_another_transport,
            created_date: this.created_date,
            modified_date: this.modified_date,
            state: this.state,
            driver_tags: this.driver_tags,
            driver_fortnight_earnings: this.driver_fortnight_earnings,
            concurrent_transports: this.concurrent_transports
        };
    }

    copyWith(updates: Partial<BudgetDto>): BudgetDto {
        return new BudgetDto({
            ...this.toJson(),
            ...updates
        });
    }

    private static toStringValue(value: string | number | null | undefined, fallback = ''): string {
        if (value === null || value === undefined) {
            return fallback;
        }
        return String(value);
    }
}

export class CalculateMarginRequestDto {
    transport_id: number = 0;
    driver_id: number = 0;
    base_price: string = '0.00';
    fuel_price: string = '0.00';
    transport_cost_outbound: string = '0.00';
    transport_cost_return: string = '0.00';
    optional_expenses_price: string = '0.00';
    toll_expense: string = '0.00';
    ferry_cost: string = '0.00';
    electric_charge_cost?: string = '0.00';

    constructor(data: Partial<CalculateMarginRequestDto> = {}) {
        Object.assign(this, data);
    }
}

export class CalculateMarginResponseDto {
    transport_id: number = 0;
    driver_id: number = 0;
    transport_price: string = '0.00';
    driver_cost: string = '0.00';
    margin_eur: string = '0.00';
    margin_pct: string = '0.00';

    static fromJson(json: any): CalculateMarginResponseDto {
        const dto = new CalculateMarginResponseDto();
        if (!json) return dto;
        dto.transport_id = json.transport_id ?? 0;
        dto.driver_id = json.driver_id ?? 0;
        dto.transport_price = json.transport_price?.toString() ?? '0.00';
        dto.driver_cost = json.driver_cost?.toString() ?? '0.00';
        dto.margin_eur = json.margin_eur?.toString() ?? '0.00';
        dto.margin_pct = json.margin_pct?.toString() ?? '0.00';
        return dto;
    }
}


